<template>
  <!--
    DemoAccessModal — entrar a la app como un perfil demo (docs/BE-RESPONSE-DEMO-ACCESS.md).
      1. credenciales de staff (las del backoffice) → abre la sesión de demo;
      2. perfiles agrupados por tipo de dashboard (empresa, grupo, naviera,
         advisor, auditor) → entra como ese usuario.
    Si ya hay una sesión de demo vigente abre directo en el paso 2 (cambiar de
    perfil sin volver a escribir la contraseña).
  -->
  <DsModal
    :model-value="modelValue"
    :title="title"
    :size="step === 'credentials' ? 'md' : 'xl'"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="demo-acc__head">
        <span class="demo-acc__head-icon"><component :is="step === 'credentials' ? 'PhLockKey' : 'PhUsersThree'" :size="20" /></span>
        <div>
          <span class="demo-acc__eyebrow">{{ $t('demoAccess.eyebrow') }}</span>
          <h2 class="demo-acc__title">{{ title }}</h2>
        </div>
      </div>
    </template>

    <!-- 1 · Credenciales de staff -->
    <form v-if="step === 'credentials'" class="demo-acc__form" novalidate @submit.prevent="submitCredentials">
      <p class="demo-acc__lede">{{ $t('demoAccess.credentialsLede') }}</p>
      <DsField :label="$t('demoAccess.email')">
        <DsInput v-model="email" type="email" autocomplete="username" :error="!!error" />
      </DsField>
      <DsField :label="$t('demoAccess.password')" :error="errorText">
        <DsInput v-model="password" type="password" autocomplete="current-password" :error="!!error" />
      </DsField>
      <DsButton type="submit" :loading="loading" :disabled="loading || !email || !password" class="demo-acc__submit">
        {{ $t('demoAccess.continue') }}
        <template #trailing><PhArrowRight :size="16" /></template>
      </DsButton>
      <p class="demo-acc__note"><PhShieldCheck :size="14" /> {{ $t('demoAccess.auditNote') }}</p>
    </form>

    <!-- Cargando perfiles -->
    <div v-else-if="step === 'loading'" class="demo-acc__grid demo-acc__grid--loading">
      <DsSkeleton v-for="i in 4" :key="i" height="148px" border-radius="var(--radius-md)" />
    </div>

    <!-- 2 · Perfiles: una sola grilla, ordenada por tipo de dashboard. El tipo va
         dentro de cada tarjeta (casi siempre hay un perfil por tipo, y una
         sección por tarjeta dejaba media grilla vacía). -->
    <div v-else class="demo-acc__personas">
      <p v-if="!personas.length" class="demo-acc__empty">{{ $t('demoAccess.noPersonas') }}</p>
      <div v-else class="demo-acc__grid">
        <button
          v-for="p in sortedPersonas"
          :key="p.id"
          type="button"
          :class="['demo-acc__card', { 'is-current': p.id === currentPersonaId }]"
          :disabled="loading"
          @click="choose(p)"
        >
          <span class="demo-acc__card-top">
            <span class="demo-acc__avatar">
              <img v-if="p.company?.logo && !brokenLogos[p.id]" :src="p.company.logo" alt="" @error="brokenLogos[p.id] = true" />
              <template v-else>{{ initials(p) }}</template>
            </span>
            <span class="demo-acc__kind">
              <component :is="KIND_ICON[kindOf(p)]" :size="14" />
              {{ $t(`demoAccess.kind.${kindOf(p)}`) }}
            </span>
            <span v-if="p.id === currentPersonaId" class="demo-acc__current">{{ $t('demoAccess.current') }}</span>
            <PhSpinner v-else-if="choosing === p.id" :size="18" class="demo-acc__spin" />
            <PhArrowRight v-else :size="18" class="demo-acc__go" />
          </span>
          <span class="demo-acc__card-body">
            <strong>{{ text(p.label) }}</strong>
            <span v-if="text(p.description)" class="demo-acc__card-desc">{{ text(p.description) }}</span>
          </span>
          <span class="demo-acc__card-meta">
            <span class="demo-acc__company">{{ p.company?.name }}</span>
            <span v-if="ROLE_KEY[p.user?.role]" class="demo-acc__tag">{{ $t(ROLE_KEY[p.user.role]) }}</span>
            <span v-if="p.company?.subscriptionType" class="demo-acc__tag">{{ p.company.subscriptionType }}</span>
          </span>
        </button>
      </div>
      <p v-if="errorText" class="demo-acc__error" role="alert">{{ errorText }}</p>
    </div>

    <template v-if="step === 'personas'" #footer>
      <div class="demo-acc__foot">
        <span class="demo-acc__session">
          {{ $t('demoAccess.sessionOf', { name: session?.staffName || '—', time: expiresTime }) }}
        </span>
        <button type="button" class="demo-acc__end" :disabled="loading" @click="endDemo">
          <PhSignOut :size="16" /> {{ $t('demoAccess.end') }}
        </button>
      </div>
    </template>
  </DsModal>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import {
  PhArrowRight, PhBuildings, PhCompass, PhLockKey, PhSealCheck, PhShieldCheck, PhShippingContainer, PhSignOut, PhSpinner, PhStorefront, PhUsersThree,
} from '@phosphor-icons/vue';
import DsButton from '@/components/ds/DsButton.vue';
import DsField from '@/components/ds/DsField.vue';
import DsInput from '@/components/ds/DsInput.vue';
import DsModal from '@/components/ds/DsModal.vue';
import DsSkeleton from '@/components/ds/DsSkeleton.vue';
import { Role } from '@/composables/useRoles';
import { homeRouteFor } from '@/utils/appSession';
import { useDemoAccess, hasDemoSession } from '@/composables/useDemoAccess';

// Ícono de cada tipo de dashboard (`kind` del catálogo).
const KIND_ICON = {
  company: PhStorefront,
  group: PhBuildings,
  network: PhShippingContainer,
  advisor: PhCompass,
  auditor: PhSealCheck,
  other: PhUsersThree,
};
const ROLE_KEY = {
  [Role.Owner]: 'demoAccess.role.owner',
  [Role.Admin]: 'demoAccess.role.admin',
  [Role.Editor]: 'demoAccess.role.editor',
  [Role.Auditor]: 'demoAccess.role.auditor',
  [Role.Advisor]: 'demoAccess.role.advisor',
};
// Códigos del API → mensaje.
const ERROR_KEY = {
  INVALID_CREDENTIALS: 'demoAccess.errors.credentials',
  FORBIDDEN_PERMISSION: 'demoAccess.errors.permission',
  ACCOUNT_LOCKED: 'demoAccess.errors.locked',
  RATE_LIMITED: 'demoAccess.errors.locked',
  DEMO_SESSION_EXPIRED: 'demoAccess.errors.expired',
  PERSONA_NOT_FOUND: 'demoAccess.errors.persona',
  PERSONA_UNAVAILABLE: 'demoAccess.errors.persona',
};

export default {
  name: 'DemoAccessModal',
  components: {
    DsButton, DsField, DsInput, DsModal, DsSkeleton,
    PhArrowRight, PhLockKey, PhShieldCheck, PhSignOut, PhSpinner, PhUsersThree,
  },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const router = useRouter();
    const demo = useDemoAccess();
    const { personas, session, persona, loading, error } = demo;

    const step = ref('credentials'); // credentials | loading | personas
    const email = ref('');
    const password = ref('');
    const choosing = ref(null);

    const title = computed(() => (step.value === 'credentials' ? t('demoAccess.titleLogin') : t('demoAccess.titlePick')));
    const errorText = computed(() => (error.value ? t(ERROR_KEY[error.value] || 'demoAccess.errors.generic') : ''));
    const currentPersonaId = computed(() => persona.value?.personaId ?? null);
    const expiresTime = computed(() => (session.value?.expiresAt
      ? new Date(session.value.expiresAt).toLocaleTimeString(locale.value, { hour: '2-digit', minute: '2-digit' })
      : '—'));

    const text = (o) => {
      if (!o) return '';
      const lang = String(locale.value || 'es').slice(0, 2);
      return o[lang] || o.es || o.en || '';
    };
    const initials = (p) => String(p.company?.name || text(p.label) || '?')
      .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

    const kindOf = (p) => (KIND_ICON[p.kind] ? p.kind : 'other');
    // Orden del backoffice: el sortOrder del API (la lista ya viene así; el sort
    // estable lo asegura si cambia). Sin sortOrder, se respeta el orden recibido.
    const sortedPersonas = computed(() => [...personas.value]
      .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0)));
    const brokenLogos = reactive({});

    // Al abrir: con demo vigente, directo a los perfiles; si venció, credenciales.
    watch(() => props.modelValue, async (open) => {
      if (!open) return;
      error.value = '';
      if (hasDemoSession()) {
        step.value = 'loading';
        const ok = await demo.loadPersonas();
        step.value = ok ? 'personas' : 'credentials';
      } else {
        step.value = 'credentials';
      }
    }, { immediate: true });

    const submitCredentials = async () => {
      if (await demo.start(email.value.trim(), password.value)) {
        password.value = '';
        step.value = 'personas';
      }
    };

    // Entra como el perfil y recarga la app en su dashboard (recarga completa:
    // así ningún store conserva datos del perfil anterior).
    const choose = async (p) => {
      if (p.id === currentPersonaId.value) { emit('update:modelValue', false); return; }
      choosing.value = p.id;
      const user = await demo.loginAs(p.id);
      choosing.value = null;
      if (!user) {
        if (!hasDemoSession()) step.value = 'credentials';
        return;
      }
      window.location.assign(router.resolve({ name: homeRouteFor(user) }).href);
    };

    const endDemo = async () => {
      await demo.end();
      window.location.assign(router.resolve({ name: 'LoginRoute', query: { demo: '1' } }).href);
    };

    return {
      step, title, email, password, loading, errorText, personas, sortedPersonas, kindOf, brokenLogos, session, expiresTime,
      currentPersonaId, choosing, text, initials, submitCredentials, choose, endDemo, KIND_ICON, ROLE_KEY,
    };
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/components/demo/demo-access";
</style>
